import { pageSourceColumns } from '../db/workspace-tables'
import { lexiconTrigrams, normalizeLexiconTrigramText, lexiconPrefixPattern } from './lexicon-trigram'
import { readPageDocument } from '../db/page-document'
import type { WorkspacePage } from '../db/workspace-tables'
import { crdt } from '@w/crdt'
import type { ClientDb } from '../db/db'
import type { LexiconCacheDb } from './lexicon-cache-db'
import type {
  CachedEntry,
  Language,
  LexiconEntry,
  LexiconHit,
} from './lexicon-model'
import { LEXICON_TRIGRAM_THRESHOLD, lexiconTrigramMatch } from './lexicon-cache-search'
import { lexiconMatchTier, normalizeLexiconText } from './lexicon-cache-search-text'

export type LexiconSearchDeps = {
  db: ClientDb
  workspaceId: string
}

type _CacheSearchDeps = {
  db: LexiconCacheDb
}

export function searchLexicon(
  deps: LexiconSearchDeps,
  query: { language: Language; text: string; limit?: number },
): Promise<LexiconHit[]>
export function searchLexicon(
  deps: _CacheSearchDeps,
  query: { language: Language; text: string; limit?: number },
): Promise<LexiconHit[]>
export function searchLexicon(
  deps: LexiconSearchDeps | _CacheSearchDeps,
  query: { language: Language; text: string; limit?: number },
): Promise<LexiconHit[]> {
  return 'cachedEntries' in deps.db
    ? _searchCache(deps as _CacheSearchDeps, query)
    : _searchPages(deps as LexiconSearchDeps, query)
}

async function _searchPages(
  deps: LexiconSearchDeps,
  query: { language: Language; text: string; limit?: number },
): Promise<LexiconHit[]> {
  const text = normalizeLexiconTrigramText(query.text).trim()
  const limit = query.limit ?? 20
  if (lexiconTrigrams(text).length === 0 || limit <= 0) return []
  const rows = await lexiconPageSearchQuery(deps.db, query).execute(deps.db.pages.__selectSchema)
  return rows.map(row => lexiconPageHit(row, text))
}

/** Rank indexed postings before the only document projection. Also owns UI live-query dependencies. */
export function lexiconPageSearchQuery(db: ClientDb, query: { language: Language; text: string; limit?: number }) {
  const text = normalizeLexiconTrigramText(query.text).trim()
  const limit = lexiconTrigrams(text).length > 0 ? Math.max(0, query.limit ?? 20) : 0
  // The engine's top-k runs before SQL filters/tiers. Admit every posting hit
  // so only the final SQL LIMIT can discard an eligible headword.
  const candidateLimit = limit > 0 ? Number.MAX_SAFE_INTEGER : 0
  const candidates = db.query`
    SELECT ${db.pages.id}, hits.similarity
    FROM trigram_search('lex_trgm', ${text}, ${candidateLimit}, ${LEXICON_TRIGRAM_THRESHOLD}) hits
    JOIN ${db.pages} ON pages.rowid = hits.rowid
    WHERE ${db.pages.language.eq(query.language)} AND ${db.pages.kind.eq('lexiconEntry')}
      AND ${db.pages.deletedAt.isNull()}
    ORDER BY CASE
      WHEN ${db.pages.headwordKey} = ${text} THEN 0
      WHEN ${db.pages.headwordKey} LIKE ${lexiconPrefixPattern(text)} ESCAPE ${'\\'} THEN 1
      ELSE 2 END, hits.similarity DESC, hits.rowid
    LIMIT ${limit}`
  return db.query`
    WITH displayed AS MATERIALIZED (${candidates})
    SELECT ${pageSourceColumns(db.pages)} FROM ${db.pages}
    WHERE ${db.pages.id} IN (SELECT id FROM displayed)
    ORDER BY CASE
      WHEN ${db.pages.headwordKey} = ${text} THEN 0
      WHEN ${db.pages.headwordKey} LIKE ${lexiconPrefixPattern(text)} ESCAPE ${'\\'} THEN 1
      ELSE 2 END,
      (SELECT similarity FROM displayed WHERE id=pages.id) DESC, pages.rowid`
}

export function lexiconPageHit(row: WorkspacePage, text: string): LexiconHit {
  return _pageHit(row, entry => lexiconTrigramMatch(entry, text))
}

function _pageHit(
  row: WorkspacePage,
  match: (entry: LexiconEntry) => Pick<LexiconHit, 'matchedField' | 'matchedForm' | 'matchedTranslation'>,
): LexiconHit {
  const { content } = readPageDocument(crdt.load(row.data).toJSON(), row)
  if (content.kind !== 'lexiconEntry') throw new Error('Expected lexicon entry page')
  const { entry } = content
  const hit: LexiconHit = {
    entryId: entry.id, kind: entry.kind, headword: entry.headword,
    transcription: entry.transcription, translations: Object.assign({}, entry.translations),
    origin: 'pages', pageId: row.id,
  }
  if (entry.kind === 'lexeme') hit.pos = entry.pos
  return Object.assign(hit, match(entry))
}

async function _searchCache(
  deps: _CacheSearchDeps,
  query: { language: Language; text: string; limit?: number },
): Promise<LexiconHit[]> {
  const text = normalizeLexiconText(query.text)
  const limit = query.limit ?? 20
  if (lexiconTrigrams(text).length === 0 || limit <= 0) return []
  const { db } = deps
  const rows = await db.fn.searchEntries(query)
  return rows.map(entry => _cacheHit(entry, lexiconTrigramMatch(entry, query.text)))
}

function _cacheHit(entry: CachedEntry, match: Pick<LexiconHit, 'matchedField' | 'matchedForm' | 'matchedTranslation'>): LexiconHit {
  const hit: LexiconHit = {
    entryId: entry.id, kind: entry.kind, headword: entry.headword,
    transcription: entry.transcription, translations: Object.assign({}, entry.translations),
    origin: 'cache',
  }
  if (entry.kind === 'lexeme') hit.pos = entry.pos
  return Object.assign(hit, match)
}

export function rankHits(queryText: string, hits: LexiconHit[]): LexiconHit[] {
  const normalizedQuery = normalizeLexiconText(queryText)
  const preferred = new Map<string, { hit: LexiconHit; inputIndex: number }>()
  for (const [inputIndex, hit] of hits.entries()) {
    const existing = preferred.get(hit.entryId)
    if (
      !existing ||
      (hit.origin === 'pages' && existing.hit.origin !== 'pages')
    ) {
      preferred.set(hit.entryId, { hit, inputIndex })
    }
  }
  return [...preferred.values()]
    .toSorted((left, right) => {
      if (left.hit.origin !== right.hit.origin) return left.hit.origin === 'pages' ? -1 : 1
      return lexiconMatchTier(left.hit, normalizedQuery) - lexiconMatchTier(right.hit, normalizedQuery) || left.inputIndex - right.inputIndex
    })
    .map(({ hit }) => hit)
}
